import { BeaconStudentListSortField, BeaconSurveyStatus, TriageCategory } from '../../../graphQL';
import { CATEGORY_LABELS } from '../../../Components/Beacon/constants';

// Shown in place of the list when a survey that predates student-search support (a V1 survey) is
// selected from the dropdown.
export const UNSUPPORTED_SURVEY_MESSAGE =
  'The selected survey was delivered on an older version of Beacon and is not compatible with this list view. Please select a more recent survey.';

// Shown when the organization has no survey this list can render — either no survey periods yet, or
// every period predates student-search support — so there is nothing to default to or select.
export const NO_SUPPORTED_SURVEYS_MESSAGE =
  'No compatible surveys are available to view for this organization.';

// Shown in place of the list when the student query fails, next to a "Try again".
export const STUDENT_LIST_LOAD_ERROR = 'Unable to load the students.';

// Pinned above the list to set expectations on data freshness — the SAI insight worker refreshes on
// a daily schedule, so a just-submitted survey will not appear until the next run.
export const STUDENT_LIST_DISCLAIMER =
  'Student list data updates every 24 hours at approximately 12:30am ET.';

// One label map for the survey status, driving both the list cell and the filter
// options, so a new status is a compile error rather than a silently missing entry.
export const beaconSurveyStatusCopy: Record<BeaconSurveyStatus, string> = {
  [BeaconSurveyStatus.Complete]: 'Complete',
  [BeaconSurveyStatus.Partial]: 'Started',
  [BeaconSurveyStatus.Pending]: 'Not started',
};

export const beaconSurveyStatusOptions = Object.values(BeaconSurveyStatus).map(status => ({
  id: status,
  label: beaconSurveyStatusCopy[status],
}));

// Triage filter rank, best to worst — matches the backend severity tiers in
// saiTriageV01.ruleSet.ts (HighNeed 1 … Stable 4, Strained outranking Drifting). Typed as a
// total record so a category added to the enum stops the build here until it is ranked.
const TRIAGE_FILTER_RANK: Record<TriageCategory, number> = {
  [TriageCategory.Stable]: 0,
  [TriageCategory.Drifting]: 1,
  [TriageCategory.Strained]: 2,
  [TriageCategory.HighNeed]: 3,
};

export const beaconTriageOptions = Object.values(TriageCategory)
  .sort((a, b) => TRIAGE_FILTER_RANK[a] - TRIAGE_FILTER_RANK[b])
  .map(category => ({
    id: category,
    label: CATEGORY_LABELS[category],
  }));

// Column keys that double as the backend `sortField` values.
export const beaconSortField = BeaconStudentListSortField;

// The list tracks the sort field as a free string; only the sortable
// columns above are valid `sortField` values for the query.
export const toBeaconSortField = (
  column: string | undefined
): BeaconStudentListSortField | undefined =>
  Object.values(BeaconStudentListSortField).find(field => field === column);

// The filter Selects emit a plain `string | number | null` id, so it is narrowed to the enum
// before it becomes a filter (an invalid value would be rejected by GraphQL).
export const toBeaconSurveyStatus = (
  value: string | number | null
): BeaconSurveyStatus | undefined =>
  Object.values(BeaconSurveyStatus).find(status => status === value);

export const toBeaconTriageCategory = (value: string | number | null): TriageCategory | undefined =>
  Object.values(TriageCategory).find(category => category === value);
